import {
  getNextAppearance,
  type AppearancePreference,
  type ResolvedTheme,
} from "../lib/appearance";

interface AppearanceToggleProps {
  preference: AppearancePreference;
  resolvedTheme: ResolvedTheme;
  onCycle: () => void;
}

const labels: Record<AppearancePreference, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
};

function AppearanceIcon({
  preference,
}: {
  preference: AppearancePreference;
}) {
  if (preference === "light") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="3.5" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42" />
      </svg>
    );
  }

  if (preference === "dark") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20.1 15.4A8.5 8.5 0 0 1 8.6 3.9 8.5 8.5 0 1 0 20.1 15.4Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4a8 8 0 0 1 0 16Z" className="appearance-icon-fill" />
    </svg>
  );
}

export function AppearanceToggle({
  preference,
  resolvedTheme,
  onCycle,
}: AppearanceToggleProps) {
  const nextPreference = getNextAppearance(preference, resolvedTheme);
  const currentDescription =
    preference === "system"
      ? `System, currently ${resolvedTheme}`
      : labels[preference];

  return (
    <button
      className="appearance-toggle"
      type="button"
      data-appearance={preference}
      aria-label={`Appearance: ${currentDescription}. Activate to use ${labels[nextPreference]} mode.`}
      title={`Appearance: ${currentDescription}`}
      onClick={onCycle}
    >
      <span className="appearance-icon">
        <AppearanceIcon preference={preference} />
      </span>
      <span className="appearance-label">{labels[preference]}</span>
    </button>
  );
}
